<template>
    <MonitorLayoutTemplate
        :table_title="table_title"
        :is_show_chart_dialog="is_show_chart_dialog"
    >
        <template #datetime>
            <QpeDatetime
                :props_date="date"
                :props_time="time"
                :name="default_value"
                @update-time="update_time"
                @change-parent-date-time="change_parent_date_time"
            />
        </template>

        <template #description>
            <SignsCalculator 
                :data="sign_data"
            />
            <MapDisplaySwitch 
                v-if="table_data.length"
                :table_id="default_value"
                :table_data="immutable_table_data"
            />
        </template>

        <template #signs>
            <MonitorSigns v-if="sign_data"
                :data="sign_data"
            />
        </template>

        <template #data_table="slot_props">
            <QpeDatatable
                :data="table_data"
                :headers="header"
                :name="default_value"
                :error_text="error_text"
                :search="slot_props.search"
                :should_draw_diagram_header="should_draw_diagram_header"
                :show_download_pdf_btn="true"
                :should_display_info_header_key_value="should_display_info_header_key_value"
                :generate_tooltop_display_info="generate_tooltop_display_info_for_table"
                :should_show_raw_html_header="should_show_raw_html_header"
                @update-time="update_time"
                @change-parent-date-time="change_parent_date_time"
                @open-chart="assign_chart_component"
                :is_show_chart_dialog_parent.sync="is_show_chart_dialog"
            >
                <template 
                    #table_description
                >
                    <div class="explanation">
                        <span>1. 「--」 表示該欄位無資料。</span><br>
                        <span>2. 此表之路段雨量來自定量降雨估計(QPE)，</span><br>
                        <span>為結合雷達與雨量站觀測資料的估計值，敬請謹慎使用。</span>
                    </div>
                </template>

                <template #diagram_dialog="{ close_dialog }">
                    <template v-if="history_data_loaded && is_show_chart_dialog">
                        <MonitorQpe
                            :timeseries_data="history_data" 
                            :default_variable="qpe_history_option"
                            @close-chart-dialog="close_dialog"
                        />
                    </template>
                </template>
            </QpeDatatable>
        </template>
    </MonitorLayoutTemplate>
</template>

<script>
    import { redirect_monitor_table_ajax, station_history_ajax } from "@/utils/monitor/monitor.js"
    import { monitor_mixin } from "@/utils/monitor/monitor_mixin.js"
    import { mapState  } from 'vuex';
    import { sort_table_data, reconstruct_alert_content } from "@/utils/monitor/common_tool"
    import { monitor_signs_normal_mapping } from "@/config/monitor_config.js"
    import MonitorLayoutTemplate from "@/components/monitor/_tool/layout_template.vue"
    import SignsCalculator from "@/components/monitor/_tool/signs_calculator.vue"
    import MapDisplaySwitch from "@/components/monitor/_tool/map_display_switch.vue"
    import MonitorSigns from "@/components/monitor/_tool/monitor_signs.vue"
    import QpeDatetime from "@/components/monitor/qpe_datetime.vue"
    import QpeDatatable from "./qpe_datatable.vue"
    import MonitorQpe from "@/components/chart/monitor_qpe.vue"

    export default {
        name: 'CustomTemplate',

        components: {
            MonitorLayoutTemplate,
            QpeDatetime,
            SignsCalculator,
            MapDisplaySwitch,
            MonitorSigns,
            QpeDatatable,
            MonitorQpe
        },

        mixins: [monitor_mixin],

        data() {
            return {
                header: [],
                table_data: [],
                error_text: '',
                search: '',
                immutable_table_data: [],

                // 警示
                should_show_raw_html_header: ['alarm'],

                // 警示理由
                should_display_info_header_key_value: {
                    'alarm_reason': {}
                },

                // 時序圖相關 (數值點下去可開啟dialog)
                should_draw_diagram_header:[], // 放在array裡的欄位資料會變成一個連結，點擊之後可出現彈出視窗
                current_table_header: '',
                qpe_history_option: '',
                history_data_loaded: false, 
                is_show_chart_dialog: false, // 控制彈出視窗開合
                history_data: [], // 拿來放 station_history_ajax 從API取得的資料
            }
        },
        
        computed: {
            ...mapState('monitor', [
                'monitor_title',
                'custom_header',
                'monitor_type'
            ]),

            table_title() {
                return this.monitor_title;
            },

            default_value() {
                return this.monitor_type;
            },
        
            sign_data() {
                return this.immutable_table_data.map((item) => {
                    const each_sign_data = {
                        idx: item['item_num'] || item['item_index'],
                        signs_num: monitor_signs_normal_mapping[item['alarm']] || '-'
                    }
                    return each_sign_data
                });
            },
        },

        watch: {
            monitor_type() {
                this.init_table();
            }
        },

        async created() {
            this.init_table();
        },
        
        methods: {
            async init_table() {
                // notice: 參照 child_nomal_alertVue.vue 勤和版
                await redirect_monitor_table_ajax.call(this, this.default_value)
                
                // 模組化版的都改成使用 api_key當作唯一 key，未來如果有針對某個 key name 的特殊欄位處理，要記得要中文換成唯一 key
                this.header = this.custom_header.map(({api_key, display_name}) => {
                    return {
                        text: display_name,
                        value: api_key,
                        align: 'center'
                    }
                })
                this.should_draw_diagram_header = this.get_should_draw_diagram_header();
            },

            generate_table_data(data) {
                const new_data = JSON.parse(JSON.stringify(data))
                const sorted_data = sort_table_data(new_data, monitor_signs_normal_mapping)
                this.table_data = reconstruct_alert_content(sorted_data, this.should_show_raw_html_header);
                this.immutable_table_data = new_data;

                // 切到無資料時段後 monitor_table_ajax 會把 header 清空；
                // 切回有資料時段時要在這裡重新補上，不然 header 會永遠停留在空陣列，
                // 畫面會卡在讀取中 (qpe_datatable.vue 的 table_data_structure 需要 headers.length > 0 才會渲染)
                this.header = this.custom_header.map(({api_key, display_name}) => {
                    return {
                        text: display_name,
                        value: api_key,
                        align: 'center'
                    }
                })
            },

            generate_tooltop_display_info_for_table(row,item) {
                // 警示條件；顯示在警示理由的 tooltip
                return row['alarm_condition']
            },

            get_should_draw_diagram_header() {
                if (this.default_value == 265) {
                    return ['qpe_1h_max', 'qpe_1h_ave', 'qpe_3h_ave', 'qpe_6h_ave', 'qpe_12h_ave', 'qpe_24h_ave', 'qpe_72h_ave'];
                }
            },

            // 開啟彈出視窗後要執行的內容
            assign_chart_component(header_name, choosed_value, current_row_data) {
                this.current_table_header = header_name;
                this.qpe_history_option = this.current_table_header.split('(')[0].trim();
                this.history_data_loaded = false;
                const call_history_time = `${this.date} ${this.time}:00`;
                station_history_ajax.call(this, `qa0${current_row_data['item_num']}`, call_history_time, 'qpe');
            },
        },
    }
</script>

<style scoped>
    .description {
        padding: 5px 10px;
    }
</style>